import { useEffect, useRef, useState } from 'react';

/**
 * Scroll reveal hook.
 * Returns [ref, isVisible] for a section element.
 * Uses IntersectionObserver to detect when the element enters the viewport.
 * Once revealed, stays revealed (no hide on scroll out).
 */
export function useScrollReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

/**
 * Returns a CSS class string for the reveal animation.
 * When isVisible is true, the element becomes fully visible.
 * Includes a subtle delay option for staggered children.
 */
export function getRevealClass(isVisible: boolean, delay = 0) {
  const base = 'transition-all duration-[800ms] ease-out will-change-transform';
  const visible = isVisible
    ? 'opacity-100 translate-y-0'
    : 'opacity-0 translate-y-8';
  const delayStyle = delay > 0 ? ` delay-${delay}` : '';
  return `${base} ${visible}${delayStyle}`;
}